// MobileFolderDrawer — 모바일(좁은 화면) 폴더 서랍. 한 단계씩 들어가는 방식.
// 맨 위 단계: 홈 / 「전체 노트」(+ 폴더 추가) / 미분류 / 1단계 폴더 / 휴지통.
// 폴더 안: 위쪽에 현재 경로와 «상위 폴더로» 단추 → 첫 행 «이 폴더 노트 보기» → 하위 폴더.
// 하위가 있는 행에만 오른쪽 화살표(«하위 폴더 열기»). 행 본문을 누르면 그 폴더를 열고(부모가 서랍을 닫는다).
// 관리 단추 규칙(어느 폴더에 무엇을 보일지)은 데스크톱과 같은 folderCaps 다 — 좁은 폭에 다섯 단추를 늘 펼치면
// 이름이 안 보여서, 행의 «폴더 관리»(⋯)를 누르면 그 행 아래에 같은 이름의 단추 줄이 열린다.
// 🔴 스크롤 상자는 data-testid="meeting-folder-tree"·className 'sb-scroll'·overflowY auto·minHeight 0 을 지킨다
//    (MeetingsPage.test · tests/e2e/test_meetings_list_scroll.py 가 이 자리와 `div.gw-mrow` 행 수를 본다).
// 🔴 서랍을 닫았다 열어도 마지막 단계를 유지한다 — 서랍(aside)은 닫혀도 마운트된 채라 state 가 남는다.
import { useState } from 'react';
import {
  IconArrowsMove,
  IconChevronLeft,
  IconChevronRight,
  IconDots,
  IconFileText,
  IconFolder,
  IconFolderOpen,
  IconFolderPlus,
  IconHome,
  IconPencil,
  IconShare,
  IconTrash,
  IconX,
} from '@tabler/icons-react';
import { MEETING_THEME } from '../../utils/meetingUtil';
import { buildTree, folderCaps, pathText } from '../../utils/meetingFolderTree';

const T = MEETING_THEME;
const ellipsis = { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
const iconBtn = {
  width: '30px', height: '30px', flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  background: 'transparent', border: 'none', color: T.color.iconLine, cursor: 'pointer', padding: 0, borderRadius: T.radius.md,
};

export default function MobileFolderDrawer({
  folders,
  sel,
  homeView, allView, trashView, adminView,
  allCount, unfiledCount, folderCount,
  onHome, onAll, onUnfiled, onTrash, onSelectFolder,
  onAddRoot, newFolderSlot,
  childInputParentId, childInputSlot,
  actions = {},
}) {
  const [levelKey, setLevelKey] = useState(null);       // 지금 보는 폴더(null = 맨 위 단계)
  const [manageKey, setManageKey] = useState(null);     // «폴더 관리» 단추 줄을 연 행
  const list = Array.isArray(folders) ? folders : [];
  const { roots, byKey } = buildTree(list);
  const current = levelKey != null ? byKey.get(levelKey) || null : null;   // 지워진 폴더면 맨 위로
  const countFor = (f) => (typeof folderCount === 'function' ? folderCount(f.id) : null);

  const enter = (node) => { setManageKey(null); setLevelKey(node.key); };
  const goUp = () => { setManageKey(null); setLevelKey(current?.parentKey ?? null); };

  const row = ({ rowKey, Icon, label, sub, active, count, onClick, extra, testId }) => (
    <div key={rowKey} className="gw-mrow" data-testid={testId} onClick={onClick}
      style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0, padding: '8px 6px 8px 10px', borderRadius: T.radius.md, cursor: 'pointer', background: active ? T.color.selectedBg : 'transparent', color: active ? T.color.selectedText : T.color.text }}>
      <Icon size={16} stroke={1.9} color={T.color.iconLine} style={{ flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ ...ellipsis, fontSize: T.font.base, fontWeight: active ? 700 : 500, color: active ? T.color.selectedText : T.color.text }}>{label}</div>
        {sub && <div style={{ ...ellipsis, fontSize: T.font.xs, color: T.color.faint }}>{sub}</div>}
      </div>
      {count !== null && count !== undefined && (
        <span style={{ flexShrink: 0, fontSize: T.font.xs, fontWeight: 700, color: T.color.faint, fontVariantNumeric: 'tabular-nums' }}>{count}</span>
      )}
      {extra}
    </div>
  );

  const stop = (fn) => (e) => { e.stopPropagation(); fn(); };

  const folderRow = (node) => {
    const f = node.folder;
    const caps = folderCaps(f, node.depth);
    const manageItems = [
      caps.canCreateChild && actions.addChild ? { label: '하위 폴더 추가', Icon: IconFolderPlus, run: () => actions.addChild(f) } : null,
      caps.canShare && actions.share ? { label: '공유', Icon: IconShare, run: () => actions.share(f) } : null,
      caps.canManage && actions.rename ? { label: '이름 변경', Icon: IconPencil, run: () => actions.rename(f) } : null,
      caps.canManage && actions.move ? { label: '폴더 이동', Icon: IconArrowsMove, run: () => actions.move(f) } : null,
      caps.canManage && actions.remove ? { label: '삭제', Icon: IconX, run: () => actions.remove(f) } : null,
    ].filter(Boolean);
    const open = manageKey === node.key;
    const hasChildren = node.children.length > 0;
    return (
      <div key={node.key}>
        {row({
          rowKey: `row-${node.key}`,
          Icon: IconFolder,
          label: f.name,
          sub: f.role !== 'owner' ? `${f.owner_name || '?'} 공유 · ${f.role === 'edit' ? '편집' : '읽기'}` : null,
          active: sel?.id != null && String(sel.id) === node.key && !trashView && !adminView,
          count: countFor(f),
          onClick: () => onSelectFolder?.(f),
          extra: (
            <>
              {manageItems.length > 0 && (
                <button type="button" aria-label="폴더 관리" title="폴더 관리" aria-expanded={open}
                  onClick={stop(() => setManageKey(open ? null : node.key))} style={iconBtn}>
                  <IconDots size={16} />
                </button>
              )}
              {hasChildren && (
                <button type="button" aria-label="하위 폴더 열기" title="하위 폴더 열기"
                  onClick={stop(() => enter(node))} style={iconBtn}>
                  <IconChevronRight size={17} />
                </button>
              )}
            </>
          ),
        })}
        {open && (
          <div role="group" aria-label={`${f.name} 관리`} style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', padding: '0 6px 6px 34px' }}>
            {manageItems.map((it) => (
              <button key={it.label} type="button" aria-label={it.label} title={it.label}
                onClick={stop(() => { setManageKey(null); it.run(); })}
                style={{ ...iconBtn, width: 'auto', height: '28px', gap: '4px', padding: '0 8px', border: `1px solid ${T.color.border}`, background: T.color.surface, color: it.label === '삭제' ? T.color.danger : T.color.text, fontSize: T.font.xs, fontWeight: 700 }}>
                <it.Icon size={14} /><span>{it.label}</span>
              </button>
            ))}
          </div>
        )}
        {childInputParentId != null && String(childInputParentId) === node.key && childInputSlot}
      </div>
    );
  };

  const currentPath = current ? pathText(list, current.folder.id) : '';

  return (
    <>
      {current && (
        <div data-testid="folder-drawer-head" style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0, padding: '10px 10px 8px', borderBottom: `1px solid ${T.color.borderSoft}` }}>
          <button type="button" aria-label="상위 폴더로" title="상위 폴더로" onClick={goUp} style={iconBtn}>
            <IconChevronLeft size={18} />
          </button>
          <div data-testid="folder-drawer-path" title={currentPath} style={{ ...ellipsis, flex: 1, fontSize: T.font.sm, fontWeight: 700, color: T.color.text }}>{currentPath}</div>
        </div>
      )}
      <div className="sb-scroll" data-testid="meeting-folder-tree" style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '6px 10px' }}>
        {current ? (
          <>
            {row({
              rowKey: 'this-folder',
              testId: 'folder-drawer-open-current',
              Icon: IconFolderOpen,
              label: '이 폴더 노트 보기',
              active: sel?.id != null && String(sel.id) === current.key && !trashView && !adminView,
              count: countFor(current.folder),
              onClick: () => onSelectFolder?.(current.folder),
            })}
            {childInputParentId != null && String(childInputParentId) === current.key && childInputSlot}
            {current.children.map(folderRow)}
          </>
        ) : (
          <>
            {row({ rowKey: 'home', Icon: IconHome, label: '홈', active: homeView && !trashView && !adminView, onClick: onHome })}
            {row({
              rowKey: 'all',
              Icon: IconFileText,
              label: '전체 노트',
              active: allView && !sel && !trashView && !adminView && !homeView,
              count: allCount,
              onClick: onAll,
              extra: onAddRoot ? (
                <button type="button" title="폴더 추가" aria-label="폴더 추가" onClick={stop(onAddRoot)}
                  style={{ ...iconBtn, fontSize: '17px', fontWeight: 800, lineHeight: 1 }}>+</button>
              ) : null,
            })}
            {newFolderSlot}
            {row({ rowKey: 'unfiled', Icon: IconFolderOpen, label: '미분류', active: !sel && !allView && !trashView && !adminView, count: unfiledCount, onClick: onUnfiled })}
            {roots.map(folderRow)}
            <div style={{ borderTop: `1px solid ${T.color.border}`, margin: '6px 0' }} />
            {row({ rowKey: 'trash', Icon: IconTrash, label: '휴지통', active: trashView, onClick: onTrash })}
          </>
        )}
      </div>
    </>
  );
}
